import Button from "@/components/Button";
import FaqsSection from "@/components/FaqsSection";
import PremiumPageLayout from "@/components/PremiumPage/PremiumPageLayout";
import { useStores } from "@/hooks/useStores";
import useSubscriptionPermission from "@/hooks/useSubscriptionPermission";
import getCommonStaticProps from "@/lib/getCommonStaticProps";
import QueryPremiumPageIndex from "@/queries/QueryPremiumPageIndex.graphql";
import craft from "lib/craft";
import { observer } from "mobx-react";
import Head from "next/head";
import { useRouter } from "next/router";
import posthog from "posthog-js";
import React, { useEffect, useRef, useState } from "react";
import { Seomatic } from "react-seomatic";

declare global {
  interface Window {
    Rewardful: any;
  }
}

export async function getStaticProps({ previewData, preview }) {
  // Fetch common static properties
  const commonStaticProps = await getCommonStaticProps();

  const queries = [
    {
      document: QueryPremiumPageIndex,
    },
  ];

  const response = await craft(queries, preview ? previewData.token : null);

  const { pageEntry } = response[0];

  if (!pageEntry) {
    return {
      props: {
        ...commonStaticProps,
      },
      notFound: true,
    };
  }

  return {
    props: {
      ...commonStaticProps,
      pageEntry,
      seomatic: pageEntry.seomatic,
    },
    revalidate: process.env.VERCEL_ENV !== "production" ? 1 : 3600,
  };
}

const MobPlusMarketing = observer(({ pageEntry, globalFaqs }) => {
  const router = useRouter();
  const { authStore } = useStores();
  const { hasHadATrial, plan } = useSubscriptionPermission();
  const [elgibility, setEligibility] = useState("checking");
  const [canHaveTrial, setCanHaveTrial] = useState(true);
  const routerRef = useRef(router);

  useEffect(() => {
    posthog.capture("$pageview");
  }, []);

  // Redirect to the homepage if the user is on plan 2
  useEffect(() => {
    if (!routerRef.current.asPath.includes("#force")) {
      if (plan !== "fetching") {
        if ((plan as undefined) === 2) {
          routerRef.current.push("/");
        }
      }
    }
  }, [plan]);

  useEffect(() => {
    if (elgibility === "unclear") {
      setCanHaveTrial(!hasHadATrial);
    } else if (elgibility === "new") {
      setCanHaveTrial(true);
    } else {
      setCanHaveTrial(false);
    }
  }, [elgibility, hasHadATrial]);

  useEffect(() => {
    if (authStore.isLoggedOut) {
      return;
    }

    // If they're logged in, check their eligiblity. If there's an error, set it to new
    const checkEligibility = async () => {
      try {
        const response = await fetch("/api/plus/check-eligibility", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: authStore.currentUser.email,
          }),
        });
        const data = await response.json();
        if (data.error) {
          setEligibility("unclear");
          return;
        }
        const { status } = data;
        setEligibility(status);
      } catch (error) {
        setEligibility("unclear");
      }
    };

    checkEligibility();
  }, [authStore.isLoggedOut, authStore.isLoggedIn]);

  return (
    <>
      <Seomatic Head={Head} {...pageEntry.seomatic} />
      <div>
        <PremiumPageLayout layout={pageEntry.premiumLayout} />
      </div>
      <div
        style={{
          background: "linear-gradient(180deg, #D1DE26 0%, #7AD691 162.54%)",
        }}
        className="flex flex-col items-center justify-center px-4 py-[96px] text-center font-spatial lg:py-[128px]"
      >
        <div className="max-w-[343px] lg:max-w-[746px]">
          <div className="text-[32px] font-medium leading-7 text-black lg:text-5xl lg:leading-[43px]">
            Join the Mob for 3000+ delicious recipes
          </div>
          <div className="my-[26px] text-sm lg:text-lg">
            It's time to up your game in the kitchen
          </div>
          <Button
            theme="light"
            href="/onboarding"
            className="mx-auto w-[216px]"
          >
            Try Mob for free
          </Button>
        </div>
      </div>
    </>
  );
});

export default MobPlusMarketing;
